/**
 * Eraya price-test theme bridge — Spektakelstrik (Dawn-family theme).
 *
 * The Eraya PriceTestEngine only scans at init and when its public methods are
 * called; it does not watch the DOM. This file is the theme-specific glue:
 *
 *   1. Variant change  — Dawn publishes `variant-change` via pubsub (NOT a DOM
 *      event) synchronously right after it morphs #price-{sectionId}. We
 *      re-apply in that same tick, before paint, so the base price never flashes.
 *   2. Injected markup — quick-add modals, predictive search, recommendations
 *      and the cart drawer inject cards after load. A MutationObserver re-applies.
 *   3. Buy It Now      — accelerated checkout bypasses /cart/add, so the SDK's
 *      cart patch never runs and the shopper would be charged the ORIGINAL
 *      price. We intercept and route through /cart/add + /checkout.
 *
 * Price containers are marked in Liquid with `data-eraya-price-root` and carry
 * both `data-product-id` and `data-variant-id`.
 */
(function () {
  'use strict';

  var ROOT = '[data-eraya-price-root]';

  function getManager() {
    try {
      var E = window.Eraya;
      if (E && E.sdk && typeof E.sdk.getPriceTestManager === 'function') {
        return E.sdk.getPriceTestManager();
      }
    } catch (_) {}
    return null;
  }

  function applyToRoot(root) {
    var productId = root.getAttribute('data-product-id');
    var variantId = root.getAttribute('data-variant-id');
    if (!productId) return;
    var mgr = getManager();
    if (!mgr || typeof mgr.updatePricesForProduct !== 'function') return;
    try {
      mgr.updatePricesForProduct(String(productId), variantId ? String(variantId) : undefined);
    } catch (_) {}
  }

  function rescan() {
    var mgr = getManager();
    if (!mgr || typeof mgr.updatePricesOnCurrentPage !== 'function') return;
    try {
      mgr.updatePricesOnCurrentPage();
    } catch (_) {}
  }

  /* ------------------------------------------------------------------ *
   * 1. Variant change (Dawn pubsub — synchronous, pre-paint)
   * ------------------------------------------------------------------ */

  function onVariantChange(payload) {
    var data = payload && payload.data;
    if (!data || !data.variant) return;

    var variantId = String(data.variant.id);
    var sectionId = data.sectionId;

    // Dawn re-renders #price-{sectionId} from the section fetched with
    // ?variant=<new>, so the morphed root already carries the right ids. We
    // still write the variant back defensively (the fetch can race).
    var scope = document.getElementById('price-' + sectionId);
    var roots = scope
      ? scope.querySelectorAll(ROOT)
      : document.querySelectorAll('product-info[data-section="' + sectionId + '"] ' + ROOT);

    if (!roots.length) return;

    for (var i = 0; i < roots.length; i++) {
      roots[i].setAttribute('data-variant-id', variantId);
      applyToRoot(roots[i]);
    }
  }

  function subscribeToVariantChange() {
    if (typeof window.subscribe !== 'function') return false;
    var name = (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) || 'variant-change';
    try {
      window.subscribe(name, onVariantChange);
      return true;
    } catch (_) {
      return false;
    }
  }

  // pubsub.js is deferred like this file; if it hasn't defined `subscribe` yet,
  // retry briefly. The observer below is the backstop either way.
  if (!subscribeToVariantChange()) {
    var tries = 0;
    var poll = setInterval(function () {
      if (subscribeToVariantChange() || ++tries > 40) clearInterval(poll);
    }, 100);
  }

  /* ------------------------------------------------------------------ *
   * 2. Injected / re-rendered markup
   * ------------------------------------------------------------------ */

  var debounceTimer = null;
  function scheduleRescan() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(rescan, 150);
  }

  function collectRoots(node, out) {
    if (node.nodeType !== 1) return;
    if (node.matches && node.matches(ROOT)) out.push(node);
    if (typeof node.querySelectorAll === 'function') {
      var found = node.querySelectorAll(ROOT);
      for (var i = 0; i < found.length; i++) out.push(found[i]);
    }
  }

  function hasProductId(node) {
    if (node.nodeType !== 1) return false;
    if (node.hasAttribute && node.hasAttribute('data-product-id')) return true;
    return typeof node.querySelector === 'function' && node.querySelector('[data-product-id]') !== null;
  }

  if (typeof MutationObserver !== 'undefined') {
    // childList only — never attributes, or writing data-variant-id above would
    // re-trigger this observer in a loop.
    var observer = new MutationObserver(function (mutations) {
      var roots = [];
      var sawProduct = false;

      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          collectRoots(added[j], roots);
          if (!sawProduct && hasProductId(added[j])) sawProduct = true;
        }
      }

      // Known roots are re-applied SYNCHRONOUSLY: the observer callback runs as
      // a microtask before paint, so a re-rendered price never flashes its base
      // value. A debounced rescan here would land after paint.
      for (var k = 0; k < roots.length; k++) applyToRoot(roots[k]);

      // Anything else carrying a product id (third-party widgets, app-rendered
      // cards) gets a debounced full rescan.
      if (sawProduct && !roots.length) scheduleRescan();
    });

    var startObserver = function () {
      observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) startObserver();
    else document.addEventListener('DOMContentLoaded', startObserver);
  }

  /* ------------------------------------------------------------------ *
   * 3. Buy It Now / accelerated checkout
   * ------------------------------------------------------------------ */

  var BUY_NOW_SELECTOR = [
    'shopify-buy-it-now-button',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    '[data-shopify="payment-button"]',
    '.shopify-payment-button',
    '.shopify-payment-button__button',
  ].join(',');

  function normalizeId(value) {
    if (value == null) return null;
    var str = String(value);
    var slash = str.lastIndexOf('/'); // gid://shopify/ProductVariant/123
    return slash === -1 ? str : str.slice(slash + 1);
  }

  function findBuyNowTarget(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && node.matches && node.matches(BUY_NOW_SELECTOR)) return node;
    }
    return null;
  }

  function resolveForm(node) {
    var form = node.closest && node.closest('form[action*="/cart/add"]');
    if (form) return form;

    var formId = node.getAttribute && node.getAttribute('form');
    if (formId) {
      var byId = document.getElementById(formId);
      if (byId) return byId;
    }

    // The button often lives in a shadow root outside the form; fall back to
    // the product form in the same product-info, then to the page's only one.
    var info = node.closest && node.closest('product-info');
    if (info) {
      var scoped = info.querySelector('form[action*="/cart/add"]');
      if (scoped) return scoped;
    }

    var all = document.querySelectorAll('form[action*="/cart/add"]');
    return all.length === 1 ? all[0] : null;
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = findBuyNowTarget(event);
      if (!target) return;

      var mgr = getManager();
      if (!mgr || typeof mgr.getSimpleCartProperties !== 'function') return;

      var form = resolveForm(target);
      if (!form) return;

      // The Knit Kit flow (product-form.js) posts one /cart/add per selected
      // yarn colour alongside the main product. Buy It Now skips all of that,
      // so intercepting here would create an incomplete order. Leave those
      // products on the native path — dynamic checkout should be turned off
      // for them in the theme editor.
      var info = form.closest('product-info') || document;
      if (info.querySelector && info.querySelector('.product__colors')) return;

      var idInput = form.querySelector('[name="id"]');
      var variantId = normalizeId(idInput && idInput.value);
      if (!variantId) return;

      var qtyInput = form.querySelector('[name="quantity"]');
      var quantity = parseInt(qtyInput && qtyInput.value, 10);
      if (!quantity || quantity < 1) quantity = 1;

      var productId = null;
      try {
        if (typeof mgr.getProductIdFromVariantId === 'function') {
          productId = mgr.getProductIdFromVariantId(String(variantId));
        }
      } catch (_) {}
      if (!productId) {
        var root = (info.querySelector && info.querySelector(ROOT)) || null;
        productId = root && root.getAttribute('data-product-id');
      }
      if (!productId) return;

      var properties = null;
      try {
        properties = mgr.getSimpleCartProperties(String(productId), String(variantId));
      } catch (_) {}

      // FAIL OPEN: not a test product (or no properties resolved) — let the
      // native express checkout run untouched.
      if (!properties || !Object.keys(properties).length) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      var body = new FormData();
      body.append('id', variantId);
      body.append('quantity', String(quantity));
      Object.keys(properties).forEach(function (key) {
        body.append('properties[' + key + ']', properties[key]);
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: body,
      })
        .then(function (response) {
          if (!response.ok) throw new Error('cart/add failed: ' + response.status);
          return response.json();
        })
        .then(function () {
          window.location.href = '/checkout';
        })
        .catch(function () {
          // Don't strand the shopper — fall back to the cart.
          window.location.href = '/cart';
        });
    },
    { capture: true }
  );
})();
